import { useEffect, useMemo, useState } from "react";
import Avatar from "react-avatar";
import { supabase } from "../supabaseClient";
import { getFriends } from "../Data/FriendshipHelpers.jsx";
import { normalizeAvatarSrc } from "../Components/avatarUtils";
import "../styles/pages/friends-leaderboard.scss";

const SPORTS = ["All sports", "NHL", "MLB", "NBA", "NFL"];

const getPeriodStart = (period, sport = "All sports") => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (period === "week") {
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  } else {
    const seasonStartMonth = { MLB: 2, NFL: 8, NBA: 9, NHL: 9 }[sport] ?? 0;
    const year = start.getMonth() < seasonStartMonth ? start.getFullYear() - 1 : start.getFullYear();
    start.setFullYear(year, seasonStartMonth, 1);
  }
  return start;
};

const FriendsLeaderboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [groupLeagues, setGroupLeagues] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [settledBets, setSettledBets] = useState([]);
  const [period, setPeriod] = useState("week");
  const [sport, setSport] = useState("All sports");
  const [leagueId, setLeagueId] = useState("friends");
  const [friendId, setFriendId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLeaderboardData = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!authData.user) throw new Error("Please sign in to view your leaderboard.");

      const { data: user, error: userError } = await supabase
        .from("users")
        .select("public_user_id, username, avatar, name_color")
        .eq("public_user_id", authData.user.id)
        .single();
      if (userError) throw userError;
      setCurrentUser(user);

      const friendRows = await getFriends(user.public_user_id);
      setFriends(friendRows);

      const { data: memberships, error: membershipsError } = await supabase
        .from("chat_participants")
        .select("chat_id")
        .eq("user_id", user.public_user_id);
      if (membershipsError) throw membershipsError;

      const chatIds = [...new Set((memberships || []).map((row) => row.chat_id))];
      let rooms = [];
      let participants = [];
      if (chatIds.length) {
        const [{ data: roomRows, error: roomsError }, { data: participantRows, error: participantError }] = await Promise.all([
          supabase.from("chat_rooms").select("id, name, type").in("id", chatIds).eq("type", "group"),
          supabase.from("chat_participants").select("chat_id, user_id").in("chat_id", chatIds),
        ]);
        if (roomsError) throw roomsError;
        if (participantError) throw participantError;
        rooms = roomRows || [];
        participants = participantRows || [];
      }

      const memberIds = [...new Set([
        user.public_user_id,
        ...friendRows.map((friend) => friend.public_user_id),
        ...participants.map((participant) => participant.user_id),
      ])];
      const leagueRows = rooms.map((room) => ({
        id: room.id,
        name: room.name || "Friends group",
        memberIds: [...new Set([
          user.public_user_id,
          ...participants.filter((participant) => participant.chat_id === room.id).map((participant) => participant.user_id),
        ])],
      }));
      setGroupLeagues(leagueRows);

      const earliestBetYear = new Date().getFullYear() - 1;
      const [{ data: userRows, error: profilesError }, { data: creatorBets, error: creatorBetsError }, { data: friendBets, error: friendBetsError }] = await Promise.all([
        supabase.from("users").select("public_user_id, username, avatar, name_color").in("public_user_id", memberIds),
        supabase.from("bets").select("betid, creator_id, friend_id, betstatus, result, sporttype, created_at").eq("betstatus", "settled").gte("created_at", `${earliestBetYear}-01-01T00:00:00.000Z`).in("creator_id", memberIds),
        supabase.from("bets").select("betid, creator_id, friend_id, betstatus, result, sporttype, created_at").eq("betstatus", "settled").gte("created_at", `${earliestBetYear}-01-01T00:00:00.000Z`).in("friend_id", memberIds),
      ]);
      if (profilesError) throw profilesError;
      if (creatorBetsError) throw creatorBetsError;
      if (friendBetsError) throw friendBetsError;

      setProfiles(Object.fromEntries((userRows || []).map((row) => [row.public_user_id, row])));
      const uniqueBets = new Map();
      [...(creatorBets || []), ...(friendBets || [])].forEach((bet) => uniqueBets.set(bet.betid, bet));
      setSettledBets([...uniqueBets.values()]);
    } catch (loadError) {
      console.error("Failed loading friends leaderboard:", loadError);
      setError(loadError.message || "Could not load leaderboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboardData();
  }, []);

  const selectedLeague = groupLeagues.find((league) => league.id === leagueId);
  const leagueMemberIds = leagueId === "friends"
    ? [currentUser?.public_user_id, ...friends.map((friend) => friend.public_user_id)].filter(Boolean)
    : selectedLeague?.memberIds || [];
  const availableFriends = friends.filter((friend) => leagueMemberIds.includes(friend.public_user_id));
  const selectedMemberIds = friendId === "all"
    ? leagueMemberIds
    : [currentUser?.public_user_id, friendId].filter((id) => leagueMemberIds.includes(id));

  const leaders = useMemo(() => {
    if (!currentUser?.public_user_id) return [];
    const startTime = getPeriodStart(period, sport).getTime();
    const selectedIds = new Set(selectedMemberIds);
    const scores = new Map(selectedMemberIds.map((id) => [id, { wins: 0, losses: 0, bets: 0 }]));

    settledBets.forEach((bet) => {
      const createdAt = new Date(bet.created_at).getTime();
      if (!Number.isFinite(createdAt) || createdAt < startTime) return;
      if (sport !== "All sports" && bet.sporttype?.toUpperCase() !== sport) return;
      if (!selectedIds.has(bet.creator_id) || !selectedIds.has(bet.friend_id)) return;

      const result = (bet.result || "").toLowerCase();
      const winnerId = result.includes(" wins") ? bet.creator_id : result.includes(" lost") ? bet.friend_id : null;
      if (!winnerId) return;
      const loserId = winnerId === bet.creator_id ? bet.friend_id : bet.creator_id;
      const winner = scores.get(winnerId);
      const loser = scores.get(loserId);
      if (!winner || !loser) return;
      winner.wins += 1;
      winner.bets += 1;
      loser.losses += 1;
      loser.bets += 1;
    });

    return [...scores.entries()]
      .map(([id, score]) => ({
        id,
        ...score,
        username: profiles[id]?.username || (id === currentUser.public_user_id ? currentUser.username : "Friend"),
        avatar: normalizeAvatarSrc(profiles[id]?.avatar),
        nameColor: profiles[id]?.name_color,
        winRate: score.bets ? Math.round((score.wins / score.bets) * 100) : 0,
      }))
      .sort((a, b) => b.wins - a.wins || b.winRate - a.winRate || a.username.localeCompare(b.username))
      .map((leader, index) => ({ ...leader, rank: index + 1 }));
  }, [currentUser, friendId, period, profiles, selectedMemberIds, settledBets, sport]);

  const periodStart = getPeriodStart(period, sport);
  const periodLabel = period === "week"
    ? `Week of ${periodStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : sport === "All sports"
      ? `${periodStart.getFullYear()} calendar year`
      : `${sport} season · ${periodStart.toLocaleDateString(undefined, { month: "short", year: "numeric" })} onward`;

  return (
    <main className="fb-page friends-leaderboard-page">
      <section className="friends-leaderboard-panel fb-card">
        <header className="friends-leaderboard-header">
          <p className="fb-page__eyebrow">Friendly competition</p>
          <h1>Friends Leaderboard</h1>
          <p>See who’s on top in your friend circle and group chats.</p>
        </header>

        <div className="friends-leaderboard-period" role="group" aria-label="Leaderboard period">
          <button className={period === "week" ? "is-active" : ""} onClick={() => setPeriod("week")}>This week</button>
          <button className={period === "season" ? "is-active" : ""} onClick={() => setPeriod("season")}>Season</button>
        </div>

        <div className="friends-leaderboard-filters">
          <label>
            <span>League</span>
            <select className="form-select" value={leagueId} onChange={(event) => { setLeagueId(event.target.value); setFriendId("all"); }}>
              <option value="friends">All friends</option>
              {groupLeagues.map((league) => <option value={league.id} key={league.id}>{league.name}</option>)}
            </select>
          </label>
          <label>
            <span>Friend</span>
            <select className="form-select" value={friendId} onChange={(event) => setFriendId(event.target.value)}>
              <option value="all">Everyone</option>
              {availableFriends.map((friend) => <option key={friend.public_user_id} value={friend.public_user_id}>{friend.username || "Friend"}</option>)}
            </select>
          </label>
          <label>
            <span>Sport</span>
            <select className="form-select" value={sport} onChange={(event) => setSport(event.target.value)}>
              {SPORTS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
        </div>

        <div className="friends-leaderboard-caption">
          <strong>{periodLabel}</strong>
          <span>Ranked by settled wins · win rate breaks ties</span>
        </div>

        {loading ? (
          <div className="friends-leaderboard-empty" role="status">Loading standings…</div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : leaders.length === 0 || !leaders.some((leader) => leader.bets > 0) ? (
          <div className="friends-leaderboard-empty">
            <span aria-hidden="true">🏆</span>
            <h2>No settled bets yet</h2>
            <p>Once a friendly bet is settled, the standings will show here.</p>
          </div>
        ) : (
          <div className="friends-leaderboard-list">
            {leaders.map((leader) => (
              <article className={`friends-leaderboard-row ${leader.rank <= 3 ? `rank-${leader.rank}` : ""}`} key={leader.id}>
                <div className="friends-leaderboard-rank">{leader.rank <= 3 ? ["🥇", "🥈", "🥉"][leader.rank - 1] : `#${leader.rank}`}</div>
                <Avatar
                  round
                  size="44"
                  name={leader.username}
                  src={leader.avatar}
                  color={leader.nameColor || "#438f79"}
                />
                <div className="friends-leaderboard-identity">
                  <strong>{leader.username}{leader.id === currentUser?.public_user_id ? " (you)" : ""}</strong>
                  <span>{leader.wins}W – {leader.losses}L · {leader.winRate}% win rate</span>
                </div>
                <div className="friends-leaderboard-points">
                  <strong>{leader.wins}</strong>
                  <span>{leader.wins === 1 ? "win" : "wins"}</span>
                </div>
              </article>
            ))}
          </div>
        )}
        <footer className="friends-leaderboard-footer">
          <span>Standings count settled bets created in the selected period. Group chats appear as leagues.</span>
          <button className="fb-btn fb-btn--ghost" type="button" onClick={loadLeaderboardData} disabled={loading}>Refresh</button>
        </footer>
      </section>
    </main>
  );
};

export default FriendsLeaderboard;
